import { useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { PATHS } from '../../../router/paths.ts';
import { useRoles } from '@utils/hooks/use-roles.ts';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { css, StyleSheet } from 'aphrodite';
import { useEntitlements } from '@utils/providers/entitlements-context.tsx';
import { useNavigationItems } from '@utils/hooks/use-navigation-items.ts';
import Button from '@components/atoms/ui-kit/button.tsx';
import { IS_ON_PREM } from '@utils/constants/app-constants.ts';
import {
  BriefcaseBusiness,
  ChevronDown,
  ChevronRight,
  FileText,
  Sparkles,
  Subtitles,
  UploadIcon
} from 'lucide-react';
import Upload from '@components/shared/upload/upload.tsx';
import { DocumentTypeOptions } from '@utils/types/editors/utils.ts';
import { useNavigate } from 'react-router-dom';
import { ResellerOrgSwitcher } from '@components/reseller-dashboard/reseller-org-switcher.tsx';
import { SidebarShell } from '../app-shell/sidebar-shell.tsx';
import { NavItemRow } from '../app-shell/nav-item-row.tsx';
import { UserRowFooter } from '../app-shell/user-row-footer.tsx';
import { RailQuickActions } from '../app-shell/rail-actions.tsx';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';

interface SideNavProps {
  /** Render the icon rail variant (editor pages, #2230). */
  collapsed?: boolean;
  /** Toggle handler for the rail's expand/collapse button. */
  onToggleCollapsed?: () => void;
}

export default function SideNav({
  collapsed = false,
  onToggleCollapsed
}: SideNavProps = {}) {
  const { t } = useTranslation();
  const location = useLocation();
  const { getColour, getBrandTint, theme } = useThemeContext();
  const [type, setType] = useState<DocumentTypeOptions | 'align' | ''>('');
  const { isReseller, isSysOp } = useRoles();
  const { hasFeature, upgradeButtonState } = useEntitlements();
  const navigate = useNavigate();
  const { data: organization } = useOrganization();

  const isOnResellerPage = location.pathname.startsWith('/reseller');
  const [showResellerSubItems, setShowResellerSubItems] =
    useState(isOnResellerPage);

  useEffect(() => {
    if (isOnResellerPage) setShowResellerSubItems(true);
  }, [isOnResellerPage]);

  const navLinkStyles = useMemo(
    () =>
      StyleSheet.create({
        link: {
          color: getColour('normalText'),
          transition: 'background-color 0.15s ease, color 0.15s ease',
          ':hover': {
            color: getColour('activeText'),
            backgroundColor: getBrandTint(0.06)
          }
        },
        active: {
          color: getColour('activeText'),
          backgroundColor: getBrandTint(0.12)
        },
        activeParent: {
          color: getColour('activeText')
        }
      }),
    [getColour, getBrandTint, theme]
  );

  // Single source of truth — same list powers the global search Navigation bucket.
  const { main: mainNavItems, reseller: resellerSubItems } =
    useNavigationItems();

  if (collapsed) {
    // Rail is intentionally minimal per #2230 spec — logo, new, home,
    // search, library, avatar. The full main nav returns when the user
    // expands the sidebar.
    return (
      <SidebarShell
        collapsed
        onToggleCollapsed={onToggleCollapsed}
        navSlot={<RailQuickActions />}
        footerSlot={<UserRowFooter collapsed />}
      />
    );
  }

  return (
    <SidebarShell
      onToggleCollapsed={onToggleCollapsed}
      navSlot={
        <ul role="list" className="m-0 p-0 space-y-0.5">
          {mainNavItems.map(item => (
            <NavItemRow key={item.href} item={item} />
          ))}

          {isReseller && !IS_ON_PREM && (
            <>
              <li>
                <button
                  onClick={() => setShowResellerSubItems(v => !v)}
                  className={cn(
                    css(navLinkStyles.link),
                    isOnResellerPage && css(navLinkStyles.activeParent),
                    'w-full group flex items-center gap-x-3 rounded-2xl px-4 py-1.5 text-sm leading-6 font-medium focus:outline-none'
                  )}
                >
                  <BriefcaseBusiness className="h-5 w-5 shrink-0 text-inherit" />
                  <span className="flex-1 text-left">{t('resellers')}</span>
                  {showResellerSubItems ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-inherit transition-transform" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-inherit transition-transform" />
                  )}
                </button>
              </li>

              {showResellerSubItems &&
                resellerSubItems.map(item => (
                  <NavItemRow key={item.href} item={item} depth={1} />
                ))}

              {(isReseller || isSysOp) && isOnResellerPage && (
                <li
                  className="my-2 py-2"
                  style={{
                    borderTop: `1px solid ${getColour('hairline')}`,
                    borderBottom: `1px solid ${getColour('hairline')}`
                  }}
                >
                  <ResellerOrgSwitcher />
                </li>
              )}
            </>
          )}
        </ul>
      }
      bottomScrollSlot={
        <div className="w-full flex flex-col gap-y-1.5 mt-3">
          {/* Upload card */}
          <div
            className="flex items-stretch overflow-hidden rounded-2xl border border-zinc-200 dark:border-zinc-700"
            style={{ backgroundColor: getBrandTint(0.06) }}
          >
            <div className="flex-1 px-4 py-3 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <UploadIcon
                  size={15}
                  className="shrink-0"
                  style={{ color: getColour('activeText') }}
                />
                <span
                  className="text-sm font-semibold"
                  style={{ color: getColour('normalText') }}
                >
                  {t('upload')}
                </span>
              </div>
              <p
                className="text-xs leading-relaxed -mt-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('convert-audio-and-video-to-text')}
              </p>
              <Button
                onClick={() => setType('document')}
                label={
                  <span className="flex items-center justify-start gap-2">
                    <FileText
                      size={14}
                      strokeWidth={2.5}
                      className="text-purple-600 dark:text-purple-400"
                    />
                    {t('transcript')}
                  </span>
                }
                size="small"
                variant="light"
                disabled={!hasFeature('batch') && !hasFeature('translation')}
              />
              <Button
                onClick={() => setType('caption')}
                label={
                  <span className="flex items-center justify-start gap-2">
                    <Subtitles
                      size={14}
                      strokeWidth={2.5}
                      className="text-orange-500 dark:text-orange-400"
                    />
                    {t('subtitles')}
                  </span>
                }
                size="small"
                variant="light"
                disabled={!hasFeature('batch') && !hasFeature('translation')}
              />
            </div>
            {type !== '' && (
              <div
                className="h-full"
                style={{ borderLeft: `1px solid ${getColour('hairline')}` }}
              >
                <Upload type={type} setType={setType} />
              </div>
            )}
          </div>

          {/* Upgrade / Subscribe */}
          {organization &&
            !organization.is_resellee &&
            !IS_ON_PREM &&
            upgradeButtonState !== 'hidden' && (
              <div
                className="grid grid-cols-1 gap-2 pt-3 mt-1 px-3"
                style={{ borderTop: `1px solid ${getColour('hairline')}` }}
              >
                <div className="flex items-center gap-1.5 px-1">
                  <Sparkles size={13} className="shrink-0 text-amber-400" />
                  <span
                    className="text-xs font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {upgradeButtonState === 'subscribe'
                      ? t('subscribe')
                      : t('upgrade')}
                  </span>
                </div>
                <p
                  className="text-xs leading-relaxed px-1 -mt-1"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('chose-the-right-plan-for-your-needs')}
                </p>
                <Button
                  onClick={() => navigate(PATHS().SUBSCRIBE)}
                  label={
                    upgradeButtonState === 'subscribe'
                      ? t('subscribe-here')
                      : t('upgrade-now')
                  }
                  size="small"
                  className="mx-1"
                  variant="primary"
                />
              </div>
            )}
        </div>
      }
      footerSlot={<UserRowFooter />}
    />
  );
}
